import styled from 'styled-components';

import { Colors } from 'context';
import { careerType, careerLayout } from 'services/careerType';

/**
 * Общие блоки паспорта навыка (ТЗ 0070, раздел 4.7).
 *
 * 🔴 Отдельным модулем, а не по копии в каждой колонке. Паспорт разложен на
 * две колонки — «что это и мой уровень» и «где нужен»; два десятка одинаковых
 * «styled» в двух файлах разъехались бы на первой правке отступа, и один и тот
 * же экран слева и справа выглядел бы по-разному.
 *
 * Стили перенесены из прежней одноколоночной панели БЕЗ изменения поведения:
 * менялась только оболочка, а не вид блоков.
 */

export const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 12px;
  border-top: 1px solid ${Colors.system_gray};
`;

/**
 * Заголовок блока.
 *
 * 🔴 Без капса и серого. Капс на 14 px был главной причиной, по которой
 * карточка читалась плоской: в карьерном разделе заголовки нигде больше не
 * капсовые, и паспорт выглядел набором технических меток, а не документом.
 */
export const BlockTitle = styled.h3`
  ${careerType.bodyStrong()};
  color: ${Colors.black};
  margin: 0;
`;

/** Заголовок КОЛОНКИ — на ступень крупнее блочного. */
export const ColumnTitle = styled.h3`
  ${careerType.h3()};
  color: ${Colors.black};
  margin: 0;
`;

/**
 * Карточка внутри паспорта: курс, профессия, блок спроса.
 *
 * Радиус 12, как у всех карточек раздела (`careerLayout.cardRadius` = 16
 * стоит только на жёлтой hero-карточке карьерного плана), рамка вместо тени —
 * теней в разделе нет вовсе.
 */
export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
`;

/** Ряд пилюль-фактов вместо строк «Подпись: Значение». */
export const FactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

/**
 * Пилюля-факт. `accent` — для одного главного числа на блок: светлая подложка
 * и тёмный текст того же тона, а НЕ заливка `marker_green` (тёмный по тёмному
 * не читается).
 */
export const Fact = styled.span<{ $accent?: boolean }>`
  ${careerType.meta()};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $accent }) => ($accent ? 'rgba(14, 98, 69, 0.1)' : Colors.bg_gray)};
  color: ${({ $accent }) => ($accent ? Colors.marker_green : Colors.gray_dark)};
  font-weight: ${({ $accent }) => ($accent ? 600 : 400)};
`;

export const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: baseline;
`;

export const Label = styled.span`
  ${careerType.metaStrong()};
  color: ${Colors.black};
`;

export const Value = styled.span`
  ${careerType.body()};
  color: ${Colors.black};
`;

export const Meta = styled.div`
  ${careerType.meta()};
  color: ${Colors.gray_dark};
`;

export const Lead = styled.p`
  ${careerType.body()};
  color: ${Colors.black};
  margin: 0;
`;

export const Upcoming = styled.p`
  ${careerType.body()};
  color: ${Colors.marker_green};
  margin: 0;
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Chip = styled.button`
  ${careerType.meta()};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${Colors.system_gray};
  background: ${Colors.white};
  color: ${Colors.black};
  cursor: pointer;
`;

/**
 * Главное действие блока.
 *
 * 🔴 Только «btn_text»: `black` и `white` переворачиваются между темами, а
 * жёлтый — нет, и надпись исчезает в одной из них при зелёной сборке.
 *
 * 🔴 `align-self: flex-start` обязателен. В колонке-флексе кнопка иначе
 * растягивается во всю ширину и читается полосой-разделителем, а не кнопкой —
 * ровно та грабля, что уже ловилась глазами на карточках карьерного плана.
 */
export const Cta = styled.a`
  ${careerType.bodyStrong()};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  gap: 8px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 10px 18px;
  border-radius: 10px;
  background: ${Colors.main};
  color: ${Colors.btn_text};
  text-decoration: none;
`;

/**
 * Второе действие блока — контурное, не жёлтое.
 *
 * 🔴 Жёлтых кнопок в модалке может быть только ОДНА. Поймано глазами: переход
 * к вакансиям и «посмотреть курс» стояли двумя одинаковыми жёлтыми блоками
 * друг под другом, и экран переставал отвечать на вопрос «что делать
 * сейчас» — ровно то, что ТЗ 0070 запрещает требованием одного приоритетного
 * действия. Главное действие паспорта — закрыть пробел курсом; вакансии —
 * это «посмотреть, ради чего».
 */
export const GhostCta = styled.a`
  ${careerType.bodyStrong()};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  gap: 8px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid ${Colors.black};
  background: transparent;
  color: ${Colors.black};
  text-decoration: none;
`;

/** Вторичное действие: жёлтым красить нельзя — 1.33:1 на белом. */
export const SecondaryLink = styled.a`
  ${careerType.body()};
  color: ${Colors.black};
  text-decoration: underline;
`;

export const Details = styled.details`
  ${careerType.meta()};
  color: ${Colors.gray_dark};

  summary {
    cursor: pointer;
    min-height: ${careerLayout.hitTarget}px;
    display: flex;
    align-items: center;
  }
`;
